import React, { useState, useEffect } from 'react';
import {
    Container,
    Row,
    Col,
    Card,
    CardBody,
    Button,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Spinner
} from 'reactstrap';
import { Check, Star, Zap, Shield, Crown, Diamond, AlertTriangle } from 'lucide-react';
import StripePaymentForm from './StripePaymentForm';
import "../assets/css/premiumpage.css"
import { useDarkMode } from "context/DarkModeProvider"
import "../assets/css/colors.css"
import { usePremium } from 'context/PremiumProvider';
import { cancelSubscription } from '../apiHandles/user_progress.js';
import axiosInstance from 'apiHandles/axiosInstance';
import { useLanguage } from 'context/LanguageProvider';
import CryptoJS from 'crypto-js';


const PremiumPage = () => {
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const [subscriptionStatus, setSubscriptionStatus] = useState(null);
    const [isStatusLoading, setIsStatusLoading] = useState(true);
    const [statusError, setStatusError] = useState(null);
    const [showSuccessModal, setShowSuccessModal] = useState(false);

    const { selLanguage, toggleLanguage, isload } = useLanguage();

    const { darkMode } = useDarkMode();

    const { isPremium } = usePremium();

    const SECRET_KEY = process.env.REACT_APP_STRIPE_ENCRYPTION_SECRET_KEY

    function decryptData(encryptedData) {
        try {
            const key = CryptoJS.SHA256(SECRET_KEY);

            const decrypted = CryptoJS.AES.decrypt(
                encryptedData,
                key,
                {
                    mode: CryptoJS.mode.ECB,
                    padding: CryptoJS.pad.Pkcs7
                }
            );

            const decryptedString = decrypted.toString(CryptoJS.enc.Utf8);
            return JSON.parse(decryptedString);
        } catch (error) {
            console.error("Decryption failed:", error);
            return null;
        }
    }

    const formatDate = (isoString) => {
        if (!isoString) return '-';
        const date = new Date(isoString);
        return date.toLocaleString('el-GR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    useEffect(() => {
        const loadData = async () => {
            setIsStatusLoading(true);
            try {
                if (isPremium) {
                    let response = await axiosInstance.get('/user/subscriptionStatus/');
                    let decryptedData = decryptData(response.data.data);

                    let retries = 0;
                    const maxRetries = 3;

                    // Αν δεν έχει subscription, περίμενε λίγο και ξαναπροσπάθησε
                    while (!decryptedData.has_subscription && retries < maxRetries) {
                        console.log(`⏳ Waiting for subscription data... (${retries + 1}/${maxRetries})`);
                        await new Promise(resolve => setTimeout(resolve, 2000));

                        response = await axiosInstance.get('/user/subscriptionStatus/');
                        decryptedData = decryptData(response.data.data);
                        retries++;
                    }

                    setSubscriptionStatus(decryptedData);

                    console.log("📊 Subscription Status:", {
                        hasSubscription: decryptedData.has_subscription,
                        isScheduledToCancel: decryptedData.is_scheduled_to_cancel,
                        currentPeriodEnd: decryptedData.current_period_end_human,
                        status: decryptedData.status
                    });
                }
            } catch (err) {
                console.error("Error loading data:", err);
                setStatusError('Δεν ήταν δυνατή η φόρτωση της κατάστασης συνδρομής.');
            } finally {
                setIsStatusLoading(false);
            }
        };

        loadData();
    }, [isPremium]);

    const premiumFeatures = [
        {
            icon: <Zap className="text-warning" size={24} />,
            title: selLanguage ? 'Πρόσβαση στην Δημιουργία Δομής' : "Access to 'Create' feature",
            description: 'Πρόσβαση σε όλα τα quiz χωρίς περιορισμούς'
        },

        {
            icon: <Crown className="text-warning" size={24} />,
            title: selLanguage ? 'Αφαίρεση Διαφημίσεων' : "Remove advertisements",
            description: 'Πρόσβαση σε premium μαθήματα και υλικό'
        }
    ];

    const plans = [
        {
            id: 'monthly',
            name: selLanguage ? 'Μηνιαία Συνδρομή' : 'Monthly Subscription',
            price: '1.99',
            period: 'μήνα',
            savings: null,
            popular: false
        },
        /*{
            id: 'yearly',
            name: selLanguage ? 'Ετήσια Συνδρομή' : 'Yearly Subscription',
            price: '29.99',
            period: 'έτος',
            savings: selLanguage ? 'Εξοικονόμηση 25%' : 'Save 25%',
            popular: true
        }*/
    ];

    const getPlanName = (interval) => {
        if (interval === 'month') return 'Μηνιαία Συνδρομή';
        if (interval === 'year') return 'Ετήσια Συνδρομή';
        return 'Συνδρομή';
    };

    const handlePlanSelect = (plan) => {
        setSelectedPlan(plan);
        setShowPaymentModal(true);
    };

    const handlePaymentSuccess = () => {
        setShowPaymentModal(false);
        window.location.reload();
    };

    const handleCancelSubscription = async () => {
        setIsLoading(true);

        try {
            const success = await cancelSubscription();

            if (success) {
                setShowCancelModal(false);

                // ✅ Decrypt το updated status
                const response = await axiosInstance.get('/user/subscriptionStatus/');
                const updatedStatus = decryptData(response.data.data);
                setSubscriptionStatus(updatedStatus);

                // Άνοιγμα success modal αντί για alert
                setShowSuccessModal(true);
            } else {
                throw new Error('Αποτυχία ακύρωσης συνδρομής');
            }
        } catch (error) {
            console.error('Error canceling subscription:', error);
            const errorMessage = error.response?.data?.error || error.message || 'Παρουσιάστηκε πρόβλημα';
            alert(`❌ ${errorMessage}\n\nΠαρακαλώ δοκιμάστε ξανά ή επικοινωνήστε με την υποστήριξη.`);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div
            className="main-content premiumContainer"
            style={{
                background: darkMode ? "#1e293b" : "var(--lightModeXpContainer)"
            }}
        >
            <Container>
                {/* Header */}
                <div className="text-center mb-5">
                    <h1 className="display-3 font-weight-bold mb-3" style={{ color: darkMode ? "#ffffff" : "#2a313bff" }}>
                        {selLanguage ? "Αναβάθμισε στο Premium" : "Upgrade to Premium"}  <i className="ni ni-diamond mb-3" style={{ fontSize: "34px", color: "#20bde0ff" }} />
                    </h1>
                    <p className={`lead ${darkMode ? "text-white" : "text-dark"}`}>
                        {selLanguage ? "Ξεκλείδωσε όλες τις δυνατότητες και πήγαινε την εμπειρία σου στο επόμενο επίπεδο" : "Unlock all features and take your experience to the next level"}
                    </p>
                </div>

                {/* Features Grid */}
                <Row
                    className="mb-5"
                    style={{
                        display: "flex",
                        alignContent: "center",
                        justifyContent: "center"
                    }}
                >
                    {premiumFeatures.map((feature, idx) => (
                        <Col style={{ height: "40px" }} key={idx} md="6" lg="6" xl="4" className="mb-4">
                            <Card
                                className="border-0 shadow-lg h-100"
                                style={{
                                    background: "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
                                    transition: 'transform 0.3s ease',
                                    borderRadius: "20px",
                                    color: "white",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                {feature.title}
                            </Card>
                        </Col>
                    ))}
                </Row>

                <Row className="justify-content-center">
                    {plans.map((plan) => (
                        <Col key={plan.id} md="12" lg="10" xl="5" className="mb-4">
                            <Card
                                className="border-0 shadow-lg h-100 position-relative"
                                style={{
                                    background: plan.popular
                                        ? 'linear-gradient(135deg, #1e293b 0%, #334155 100%)'
                                        : 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
                                    transform: plan.popular ? 'scale(1.05)' : 'scale(1)',
                                    transition: 'all 0.3s ease',
                                    border: plan.popular ? '3px solid #fbbf24' : '2px solid #cbd5e1',
                                    boxSizing: 'border-box',
                                    borderRadius: "24px",
                                    overflow: 'hidden'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = plan.popular ? 'scale(1.08)' : 'scale(1.03)';
                                    e.currentTarget.style.boxShadow = '0 20px 50px rgba(0,0,0,0.3)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = plan.popular ? 'scale(1.05)' : 'scale(1)';
                                    e.currentTarget.style.boxShadow = '';
                                }}
                            >
                                <CardBody className="text-center p-5">
                                    <h3
                                        className="font-weight-bold mb-4"
                                        style={{
                                            color: plan.popular ? '#fff' : '#1e293b',
                                            fontSize: '1.75rem'
                                        }}
                                    >
                                        {plan.name}
                                    </h3>

                                    <div className="mb-4 mt-3">
                                        <span
                                            className="display-3 font-weight-bold"
                                            style={{
                                                color: plan.popular ? '#fbbf24' : '#3b82f6',
                                                fontSize: '3.5rem'
                                            }}
                                        >
                                            €{plan.price}
                                        </span>
                                        <span
                                            className="h5"
                                            style={{
                                                color: plan.popular ? '#cbd5e1' : '#64748b'
                                            }}
                                        >
                                            /{plan.period}
                                        </span>
                                    </div>

                                    <Button
                                        color={plan.popular ? 'warning' : 'primary'}
                                        size="lg"
                                        block
                                        onClick={() => handlePlanSelect(plan)}
                                        className="font-weight-bold mt-4"
                                        disabled={
                                            subscriptionStatus?.status === 'active' && (
                                                // Αν έχει MONTHLY → μόνο το κουμπί monthly να είναι disabled
                                                (subscriptionStatus.plan_interval === 'month' && plan.id === 'monthly') ||

                                                // Αν έχει YEARLY → όλα disabled
                                                (subscriptionStatus.plan_interval === 'year')
                                            )
                                        }
                                        style={{
                                            padding: '16px',
                                            borderRadius: '16px',
                                            fontSize: '1.1rem',
                                            background: plan.popular
                                                ? 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)'
                                                : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                                            border: 'none',
                                            boxShadow: plan.popular
                                                ? '0 8px 20px rgba(251, 191, 36, 0.3)'
                                                : '0 8px 20px rgba(59, 130, 246, 0.3)',
                                            transition: 'all 0.3s ease',
                                            opacity: (
                                                (subscriptionStatus?.status === 'active' &&
                                                    subscriptionStatus?.plan_interval === plan.id) ||
                                                (subscriptionStatus?.status === 'active' &&
                                                    subscriptionStatus?.plan_interval === 'year')
                                            ) ? 0.5 : 1,
                                            cursor: (
                                                (subscriptionStatus?.status === 'active' &&
                                                    subscriptionStatus?.plan_interval === plan.id) ||
                                                (subscriptionStatus?.status === 'active' &&
                                                    subscriptionStatus?.plan_interval === 'year')
                                            ) ? 'not-allowed' : 'pointer'
                                        }}
                                        onMouseEnter={(e) => {
                                            if (!e.currentTarget.disabled) {
                                                e.currentTarget.style.transform = 'translateY(-2px)';
                                                e.currentTarget.style.boxShadow = plan.popular
                                                    ? '0 12px 25px rgba(251, 191, 36, 0.4)'
                                                    : '0 12px 25px rgba(59, 130, 246, 0.4)';
                                            }
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.transform = 'translateY(0)';
                                            e.currentTarget.style.boxShadow = plan.popular
                                                ? '0 8px 20px rgba(251, 191, 36, 0.3)'
                                                : '0 8px 20px rgba(59, 130, 246, 0.3)';
                                        }}
                                    >
                                        {/* ✅ Δυναμικό κείμενο */}
                                        {subscriptionStatus?.status === 'active' &&
                                            subscriptionStatus?.plan_interval === plan.id
                                            ? 'Τρέχον Πακέτο'
                                            : subscriptionStatus?.status === 'active' &&
                                                subscriptionStatus?.plan_interval === 'year' &&
                                                plan.id === 'monthly'
                                                ? 'Μη Διαθέσιμο'
                                                : selLanguage ? 'Επιλογή Πακέτου' : 'Select Plan'
                                        }
                                    </Button>

                                    <br></br>
                                    {plan.savings && (
                                        <div style={{
                                            display: 'inline-block',
                                            background: 'rgba(34, 197, 94, 0.2)',
                                            color: '#22c55e',
                                            padding: '6px 16px',
                                            borderRadius: '12px',
                                            fontSize: '0.9rem',
                                            fontWeight: '600',
                                            marginBottom: '1.5rem'
                                        }}>
                                            {plan.savings}
                                        </div>
                                    )}
                                </CardBody>
                            </Card>
                        </Col>
                    ))}
                </Row>
                <br />

                {/* Success Modal */}
                <Modal
                    isOpen={showSuccessModal}
                    toggle={() => setShowSuccessModal(false)}
                    centered
                    style={{ maxWidth: '500px' }}
                >
                    <div style={{
                        background: 'linear-gradient(135deg, #167539ff 0%, #16a34a 100%)',
                        color: 'white',
                        borderRadius: '16px',
                        overflow: 'hidden'
                    }}>
                        <ModalBody style={{ padding: '3rem 2rem', textAlign: 'center' }}>
                            <div style={{
                                width: '80px',
                                height: '80px',
                                borderRadius: '50%',
                                background: 'rgba(255, 255, 255, 0.2)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                margin: '0 auto 1.5rem'
                            }}>
                                <Check size={48} color="#fff" strokeWidth={3} />
                            </div>

                            <h3 style={{
                                fontSize: '1.75rem',
                                fontWeight: '700',
                                marginBottom: '1rem',
                                color: '#fff'
                            }}>
                                {selLanguage ? 'Επιτυχής Ακύρωση!' : 'Successfully Cancelled!'}
                            </h3>

                            <p style={{
                                fontSize: '1.1rem',
                                color: 'rgba(255, 255, 255, 0.95)',
                                marginBottom: '1.5rem',
                                lineHeight: '1.6'
                            }}>
                                {selLanguage
                                    ? 'Η αυτόματη ανανέωση της συνδρομής σας απενεργοποιήθηκε επιτυχώς.'
                                    : 'Your subscription auto-renewal has been successfully disabled.'}
                            </p>

                            <div style={{
                                background: 'rgba(255, 255, 255, 0.15)',
                                borderRadius: '12px',
                                padding: '1.25rem',
                                marginBottom: '1.5rem'
                            }}>
                                <p style={{
                                    fontSize: '0.95rem',
                                    color: '#fff',
                                    margin: 0,
                                    lineHeight: '1.6'
                                }}>
                                    {selLanguage
                                        ? ' Θα συνεχίσετε να έχετε πλήρη πρόσβαση σε όλες τις premium λειτουργίες μέχρι '
                                        : ' You will continue to have full access to all premium features until '}
                                    <strong>{formatDate(subscriptionStatus?.current_period_end_human)}</strong>
                                </p>
                            </div>

                            <p style={{
                                fontSize: '0.9rem',
                                color: 'rgba(255, 255, 255, 0.85)',
                                margin: 0,
                                fontStyle: 'italic'
                            }}>
                                {selLanguage
                                    ? ' Δεν θα χρεωθείτε ξανά αυτόματα'
                                    : ' You will not be charged again automatically'}
                            </p>
                        </ModalBody>

                        <ModalFooter style={{
                            borderTop: '1px solid rgba(255, 255, 255, 0.2)',
                            padding: '1.5rem 2rem',
                            justifyContent: 'center'
                        }}>
                            <Button
                                onClick={() => setShowSuccessModal(false)}
                                style={{
                                    padding: '0.85rem 2.5rem',
                                    fontSize: '1.05rem',
                                    background: '#fff',
                                    border: 'none',
                                    borderRadius: '10px',
                                    color: '#16a34a',
                                    fontWeight: '600',
                                    transition: 'all 0.3s ease'
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.transform = 'translateY(-2px)';
                                    e.target.style.boxShadow = '0 8px 20px rgba(0, 0, 0, 0.2)';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.transform = 'translateY(0)';
                                    e.target.style.boxShadow = 'none';
                                }}
                            >
                                {selLanguage ? 'Εντάξει' : 'OK'}
                            </Button>
                        </ModalFooter>
                    </div>
                </Modal>

                {/* Cancel Subscription Button */}
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        marginBottom: '40px'
                    }}
                >
                    {isPremium && (
                        <Button
                            onClick={() => setShowCancelModal(true)}
                            disabled={isLoading || subscriptionStatus?.is_scheduled_to_cancel}
                            style={{
                                borderRadius: "15px",
                                background: "linear-gradient(135deg, #ef4444 0%, #b91c1c 100%)",
                                border: "none",
                                padding: "12px 24px",
                                fontSize: "18px",
                                color: "#fff",
                                cursor: (isLoading || subscriptionStatus?.is_scheduled_to_cancel) ? 'not-allowed' : 'pointer',
                                opacity: (isLoading || subscriptionStatus?.is_scheduled_to_cancel) ? 0.5 : 1
                            }}
                        >
                            {isLoading ? (
                                <>
                                    <Spinner size="sm" className="mr-2" />
                                    Ακύρωση σε εξέλιξη...
                                </>
                            ) : subscriptionStatus?.is_scheduled_to_cancel ? (
                                selLanguage ? 'Η Συνδρομή Έχει Ακυρωθεί' : 'Subscription Cancelled'
                            ) : (
                                selLanguage ? 'Ακύρωση Συνδρομής' : 'Cancel Subscription'
                            )}
                        </Button>
                    )}
                </div>

                {isPremium && subscriptionStatus && subscriptionStatus.has_subscription && (
                    <Row className="mb-5">
                        <Col md="12">
                            <Card style={{
                                background: darkMode ? '#334155' : "var(--lightModeModal)",
                                borderRadius: '20px',
                                padding: '2rem',
                                color: 'white'
                            }}>
                                <CardBody>
                                    <div
                                        style={{
                                            display: "flex",
                                            alignContent: "center",
                                            width: "100%",
                                            justifyContent: "center"
                                        }}>
                                        <text
                                            style={{
                                                color: darkMode ? '#ffffffff' : "#0f0e0eff",
                                                fontSize: "18px"
                                            }}>
                                            {selLanguage ? "Κατάσταση Συνδρομής" : "Subscritpion status"}
                                        </text>
                                    </div>
                                    <br></br>

                                    <Row>
                                        <Col md="4">
                                            <div className="mb-3">
                                                <small style={{ color: darkMode ? '#94a3b8' : "#0f0e0eff" }}>{selLanguage ? "Κατάσταση" : "Status"}</small>
                                                <h5 style={{
                                                    color: subscriptionStatus.status === 'active' ? '#22c55e' : '#ef4444'
                                                }}>
                                                    {subscriptionStatus.status === 'active' ? selLanguage ? ' Ενεργή' : "Active" : selLanguage ? ' Ανενεργή' : "Inactive"}
                                                </h5>
                                            </div>
                                        </Col>
                                        <Col md="4">
                                            <div className="mb-3">
                                                <small style={{ color: darkMode ? '#94a3b8' : "#0f0e0eff" }}>{selLanguage ? "Ημερομηνία Λήξης" : "Cancelation date"}</small>
                                                <h5 style={{ color: darkMode ? '#fff' : "#302f2fff" }}>
                                                    {formatDate(subscriptionStatus.current_period_end_human)}
                                                </h5>
                                            </div>
                                        </Col>
                                        <Col md="4">
                                            <div className="mb-3">
                                                <small style={{ color: darkMode ? '#94a3b8' : '#0f0e0eff' }}>{selLanguage ? "Αυτόματη Ανανέωση" : "Auto renewal"}</small>
                                                <h5 style={{
                                                    color: subscriptionStatus.is_scheduled_to_cancel ? '#ef4444' : '#22c55e'
                                                }}>
                                                    {subscriptionStatus.is_scheduled_to_cancel
                                                        ? selLanguage ? ' Απενεργοποιημένη' : "Inactive"
                                                        : selLanguage ? ' Ενεργή' : "Active"}
                                                </h5>
                                            </div>
                                        </Col>
                                    </Row>

                                    {subscriptionStatus.is_scheduled_to_cancel && (
                                        <div style={{
                                            background: 'rgba(239, 68, 68, 0.2)',
                                            border: '1px solid rgba(239, 68, 68, 0.4)',
                                            borderRadius: '12px',
                                            padding: '1rem',
                                            marginTop: '1rem'
                                        }}>
                                            <p style={{ margin: 0, color: darkMode ? '#fca5a5' : "#0f0e0eff" }}>
                                                {selLanguage ? "Η συνδρομή σας θα ακυρωθεί στις " : "Your subscription will be canceled at "}
                                                <strong>{formatDate(subscriptionStatus.current_period_end_human)}</strong>
                                            </p>
                                        </div>
                                    )}
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                )}

            </Container>

            {/* Payment Modal */}
            <Modal
                isOpen={showPaymentModal}
                toggle={() => setShowPaymentModal(false)}
                size="lg"
                centered
            >
                <div style={{
                    background: 'none',
                    color: 'white'
                }}>
                    <ModalHeader
                        toggle={() => setShowPaymentModal(false)}
                        style={{
                            borderBottom: 'none',
                            paddingBottom: '1rem',
                            background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <span style={{
                                fontSize: '1.5rem',
                                fontWeight: '600',
                                color: "#fff",
                                marginLeft: "20px"
                            }}>
                                {selLanguage ? "Ολοκλήρωση Αγοράς" : "Complete Purchase"}
                            </span>
                        </div>
                    </ModalHeader>

                    <ModalBody style={{
                        background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
                        padding: '2rem',
                        minHeight: '400px'
                    }}>
                        {selectedPlan && (
                            <div style={{
                                maxWidth: '500px',
                                margin: '0 auto'
                            }}>
                                <div style={{
                                    background: "#ecececff",
                                    padding: '1.5rem',
                                    borderRadius: '12px',
                                    marginBottom: '2rem',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                                }}>
                                    <h5 style={{
                                        fontWeight: '600',
                                        marginBottom: '1rem',
                                        color: '#333',
                                        fontSize: '1.1rem',
                                        display: "flex",
                                        alignContent: "left",
                                        marginLeft: "0px"
                                    }}>
                                        {selLanguage ? `Επιλεγμένο Πακέτο : ${selectedPlan.name}` : `Selected Plan: ${selectedPlan.name}`}
                                    </h5>
                                    <div style={{
                                        paddingTop: '1rem',

                                    }}>

                                        <div style={{
                                            marginTop: '1rem',
                                            paddingTop: '1rem',
                                            borderTop: '1px solid #034586ff'
                                        }}>
                                            <div style={{
                                                display: 'flex',
                                                alignItems: 'center'
                                            }}>
                                                <span style={{
                                                    color: '#060606ff',
                                                    fontSize: '17px',
                                                }}>
                                                    {selLanguage ? "Σύνολο:" : "Total:"}
                                                </span>
                                                <h4 style={{
                                                    margin: 0,
                                                    color: '#667eea',
                                                    fontWeight: 'bold',
                                                    fontSize: '23px',
                                                    marginLeft: '10px'
                                                }}>
                                                    {selectedPlan.price}€
                                                </h4>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div style={{
                                    backgroundColor: '#ecececff',
                                    padding: '1.5rem',
                                    borderRadius: '12px',
                                    marginBottom: '1.5rem',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                                }}>
                                    <h5 style={{
                                        fontWeight: '600',
                                        marginBottom: '1.5rem',
                                        color: '#333',
                                        fontSize: '1.1rem',
                                        marginLeft: "0px"
                                    }}>
                                        {selLanguage ? "Στοιχεία Κάρτας" : "Card Details"}
                                    </h5>
                                    <StripePaymentForm
                                        selectedPlan={selectedPlan}
                                        onSuccess={handlePaymentSuccess}
                                    />
                                </div>

                                <div style={{
                                    backgroundColor: '#e7f3ff',
                                    borderLeft: '4px solid #667eea',
                                    padding: '1rem 1.25rem',
                                    borderRadius: '8px'
                                }}>
                                    <small style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        color: '#0c5460',
                                        fontSize: '0.9rem'
                                    }}>
                                        <Shield size={18} style={{ marginRight: '0.75rem', flexShrink: 0 }} />
                                        <span>{selLanguage ? "Οι πληρωμές σας είναι ασφαλείς και κρυπτογραφημένες" : "Your payments are secure and encrypted"}</span>
                                    </small>
                                </div>
                            </div>
                        )}
                    </ModalBody>

                    <ModalFooter style={{
                        background: 'linear-gradient(135deg, #273346ff 0%, #334155 100%)',
                        borderTop: 'none',
                        padding: '1.5rem 2rem'
                    }}>
                        <div style={{
                            maxWidth: '500px',
                            margin: '0 auto',
                            width: '100%',
                            textAlign: 'right'
                        }}>
                            <Button
                                color="secondary"
                                onClick={() => setShowPaymentModal(false)}
                                style={{
                                    padding: '0.75rem 2rem',
                                    fontSize: '1rem'
                                }}
                            >
                                {selLanguage ? "Ακύρωση" : "Cancel"}
                            </Button>
                        </div>
                    </ModalFooter>
                </div>
            </Modal>


            <Modal
                isOpen={showCancelModal}
                toggle={() => !isLoading && setShowCancelModal(false)}
                centered
                style={{ maxWidth: '500px' }}
            >
                <div style={{
                    background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
                    color: 'white',
                    borderRadius: '16px',
                    overflow: 'hidden'
                }}>
                    <ModalHeader
                        toggle={() => !isLoading && setShowCancelModal(false)}
                        style={{
                            borderBottom: '2px solid rgba(239, 68, 68, 0.3)',
                            padding: '1.5rem 2rem',
                            display: 'flex',
                            alignItems: 'center'
                        }}
                    >
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            width: '100%'
                        }}>
                            <div style={{
                                width: '40px',
                                height: '40px',
                                borderRadius: '10px',
                                background: 'rgba(239, 68, 68, 0.2)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <AlertTriangle size={24} color="#ef4444" />
                            </div>
                            <span style={{
                                fontSize: '1.4rem',
                                fontWeight: '600',
                                color: '#fff'
                            }}>
                                {selLanguage ? "Ακύρωση Συνδρομής" : "Cancel Subscription"}
                            </span>
                        </div>
                    </ModalHeader>

                    <ModalBody style={{ padding: '2rem' }}>
                        <div style={{
                            background: 'rgba(239, 68, 68, 0.1)',
                            border: '1px solid rgba(239, 68, 68, 0.3)',
                            borderRadius: '12px',
                            padding: '1.5rem',
                            marginBottom: '1.5rem'
                        }}>
                            <p style={{
                                fontSize: '1.05rem',
                                marginBottom: '1.25rem',
                                color: '#fff',
                                fontWeight: '500',
                                lineHeight: '1.5'
                            }}>
                                {selLanguage
                                    ? "Είστε σίγουροι ότι θέλετε να ακυρώσετε τη συνδρομή σας;"
                                    : "Are you sure you want to cancel your subscription?"}
                            </p>
                            <ul style={{
                                listStyle: 'none',
                                padding: 0,
                                margin: 0,
                                color: '#cbd5e1'
                            }}>
                                <li style={{
                                    marginBottom: '0.85rem',
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    lineHeight: '1.5'
                                }}>
                                    <span style={{
                                        marginRight: '10px',
                                        color: '#ef4444',
                                        fontWeight: 'bold',
                                        fontSize: '1.2rem',
                                        lineHeight: '1.5'
                                    }}>•</span>
                                    <span>
                                        {selLanguage
                                            ? "Θα έχετε πρόσβαση μέχρι το τέλος της τρέχουσας περιόδου χρέωσης"
                                            : "You will have access until the end of the current billing period"}
                                    </span>
                                </li>
                                <li style={{
                                    marginBottom: '0.85rem',
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    lineHeight: '1.5'
                                }}>
                                    <span style={{
                                        marginRight: '10px',
                                        color: '#ef4444',
                                        fontWeight: 'bold',
                                        fontSize: '1.2rem',
                                        lineHeight: '1.5'
                                    }}>•</span>
                                    <span>
                                        {selLanguage
                                            ? "Δεν θα χρεωθείτε ξανά αυτόματα"
                                            : "You will not be charged again automatically"}
                                    </span>
                                </li>
                                <li style={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    lineHeight: '1.5'
                                }}>
                                    <span style={{
                                        marginRight: '10px',
                                        color: '#ef4444',
                                        fontWeight: 'bold',
                                        fontSize: '1.2rem',
                                        lineHeight: '1.5'
                                    }}>•</span>
                                    <span>
                                        {selLanguage
                                            ? "Θα χάσετε την πρόσβαση σε όλα τα premium χαρακτηριστικά"
                                            : "You will lose access to all premium features"}
                                    </span>
                                </li>
                            </ul>
                        </div>

                        <p style={{
                            fontSize: '0.95rem',
                            color: '#94a3b8',
                            textAlign: 'center',
                            fontStyle: 'italic',
                            margin: 0
                        }}>
                            {selLanguage
                                ? "Μπορείτε πάντα να επανεγγραφείτε αργότερα"
                                : "You can always resubscribe later"}
                        </p>
                    </ModalBody>

                    <ModalFooter style={{
                        borderTop: '2px solid rgba(239, 68, 68, 0.3)',
                        padding: '1.5rem 2rem',
                        display: 'flex',
                        justifyContent: 'center',
                        gap: '1rem',
                        flexWrap: 'wrap'
                    }}>
                        <Button
                            color="secondary"
                            onClick={() => setShowCancelModal(false)}
                            disabled={isLoading}
                            style={{
                                padding: '0.85rem 2rem',
                                fontSize: '1rem',
                                borderRadius: '10px',
                                minWidth: '180px',
                                fontWeight: '500',
                                background: 'rgba(148, 163, 184, 0.2)',
                                border: '1px solid rgba(148, 163, 184, 0.4)',
                                color: '#e2e8f0',
                                transition: 'all 0.3s ease'
                            }}
                            onMouseEnter={(e) => {
                                if (!isLoading) {
                                    e.target.style.background = 'rgba(148, 163, 184, 0.3)';
                                    e.target.style.transform = 'translateY(-2px)';
                                }
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.background = 'rgba(148, 163, 184, 0.2)';
                                e.target.style.transform = 'translateY(0)';
                            }}
                        >
                            {selLanguage ? "Όχι, Κράτησε τη Συνδρομή" : "No, Keep Subscription"}
                        </Button>
                        <Button
                            onClick={handleCancelSubscription}
                            disabled={isLoading}
                            style={{
                                padding: '0.85rem 2rem',
                                fontSize: '1rem',
                                background: isLoading
                                    ? 'rgba(239, 68, 68, 0.5)'
                                    : 'linear-gradient(135deg, #ef4444 0%, #b91c1c 100%)',
                                border: 'none',
                                borderRadius: '10px',
                                color: '#fff',
                                minWidth: '180px',
                                fontWeight: '500',
                                cursor: isLoading ? 'not-allowed' : 'pointer',
                                transition: 'all 0.3s ease',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '8px'
                            }}
                            onMouseEnter={(e) => {
                                if (!isLoading) {
                                    e.target.style.transform = 'translateY(-2px)';
                                    e.target.style.boxShadow = '0 8px 20px rgba(239, 68, 68, 0.4)';
                                }
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.transform = 'translateY(0)';
                                e.target.style.boxShadow = 'none';
                            }}
                        >
                            {isLoading ? (
                                <>
                                    <Spinner size="sm" />
                                    <span>{selLanguage ? "Ακύρωση..." : "Canceling..."}</span>
                                </>
                            ) : (
                                selLanguage ? "Ναι, Ακύρωσε" : "Yes, Cancel"
                            )}
                        </Button>
                    </ModalFooter>
                </div>
            </Modal>
        </div >
    );
};

export default PremiumPage;